import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RotateCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { gamesApi } from '../lib/api';
import { toast } from 'sonner';

const CHOICES = [
    { key: 'rock', label: 'Rock', emoji: '✊' },
    { key: 'paper', label: 'Paper', emoji: '✋' },
    { key: 'scissors', label: 'Scissors', emoji: '✌️' },
];

// Returns winner: 'p1' | 'p2' | 'tie'
function judge(a, b) {
    if (a === b) return 'tie';
    if (
        (a === 'rock' && b === 'scissors') ||
        (a === 'paper' && b === 'rock') ||
        (a === 'scissors' && b === 'paper')
    ) return 'p1';
    return 'p2';
}

export default function GameRPS() {
    const [p1Name, setP1Name] = useState('Player 1');
    const [p2Name, setP2Name] = useState('Player 2');
    const [started, setStarted] = useState(false);
    const [turn, setTurn] = useState('p1'); // 'p1' | 'p2' | 'reveal' | 'done'
    const [p1Choice, setP1Choice] = useState(null);
    const [p2Choice, setP2Choice] = useState(null);
    const [scores, setScores] = useState({ p1: 0, p2: 0 });
    const [round, setRound] = useState(1);
    const [winner, setWinner] = useState(null);

    const start = () => {
        setStarted(true);
        setTurn('p1');
    };

    const pickChoice = (choice) => {
        if (turn === 'p1') {
            setP1Choice(choice);
            setTurn('p2');
        } else if (turn === 'p2') {
            setP2Choice(choice);
            setTurn('reveal');
        }
    };

    const revealAndScore = async () => {
        const result = judge(p1Choice, p2Choice);
        let newScores = { ...scores };
        if (result === 'p1') newScores.p1 += 1;
        else if (result === 'p2') newScores.p2 += 1;
        setScores(newScores);
        if (newScores.p1 >= 3 || newScores.p2 >= 3) {
            const w = newScores.p1 >= 3 ? 'p1' : 'p2';
            setWinner(w);
            setTurn('done');
            try {
                await gamesApi.recordWin('rps', null, newScores[w]);
                toast.success(`🎉 ${w === 'p1' ? p1Name : p2Name} wins the match!`);
            } catch {
                toast.success(`🎉 ${w === 'p1' ? p1Name : p2Name} wins!`);
            }
        } else {
            setTimeout(() => {
                setP1Choice(null);
                setP2Choice(null);
                setRound(r => r + 1);
                setTurn('p1');
            }, 1500);
        }
    };

    const reset = () => {
        setStarted(false);
        setTurn('p1');
        setP1Choice(null);
        setP2Choice(null);
        setScores({ p1: 0, p2: 0 });
        setRound(1);
        setWinner(null);
    };

    if (!started) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-blue-950 to-slate-950 flex items-center justify-center px-4 pt-20 text-white" data-testid="rps-setup">
                <div className="max-w-md w-full">
                    <Link to="/games" className="text-sm text-slate-300 hover:text-white flex items-center gap-1 mb-4">
                        <ArrowLeft className="w-4 h-4" /> Back
                    </Link>
                    <div className="rounded-3xl bg-white/5 border border-white/10 p-6 backdrop-blur">
                        <div className="text-5xl mb-3 text-center">✊✋✌️</div>
                        <h1 className="text-2xl font-bold text-center mb-1">Rock · Paper · Scissors</h1>
                        <p className="text-sm text-slate-300 text-center mb-6">Best of 5. Pass phone between picks.</p>

                        <label className="text-xs text-slate-400 uppercase tracking-widest">Player 1</label>
                        <input
                            type="text"
                            value={p1Name}
                            onChange={(e) => setP1Name(e.target.value || 'Player 1')}
                            className="w-full mt-1 mb-4 bg-white/10 rounded-lg px-3 py-2 border border-white/10 focus:outline-none focus:border-white/30"
                            data-testid="rps-p1-name"
                        />

                        <label className="text-xs text-slate-400 uppercase tracking-widest">Player 2</label>
                        <input
                            type="text"
                            value={p2Name}
                            onChange={(e) => setP2Name(e.target.value || 'Player 2')}
                            className="w-full mt-1 mb-6 bg-white/10 rounded-lg px-3 py-2 border border-white/10 focus:outline-none focus:border-white/30"
                            data-testid="rps-p2-name"
                        />

                        <Button onClick={start} className="w-full bg-gradient-to-r from-indigo-500 to-cyan-500" data-testid="rps-start-btn">
                            Start match
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-blue-950 to-slate-950 text-white pb-20" data-testid="rps-game">
            <div className="sticky top-0 z-10 bg-slate-950/80 backdrop-blur border-b border-white/10">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/games" className="flex items-center gap-1 text-sm text-slate-300 hover:text-white" data-testid="rps-back">
                        <ArrowLeft className="w-4 h-4" /> Games
                    </Link>
                    <Button size="sm" variant="outline" onClick={reset} data-testid="rps-reset">
                        <RotateCw className="w-4 h-4 mr-1" /> Restart
                    </Button>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 pt-6">
                {/* Score */}
                <div className="grid grid-cols-3 gap-2 mb-6">
                    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-center" data-testid="rps-score-p1">
                        <div className="text-xs text-slate-400">{p1Name}</div>
                        <div className="text-3xl font-bold">{scores.p1}</div>
                    </div>
                    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-center">
                        <div className="text-xs text-slate-400">Round</div>
                        <div className="text-3xl font-bold">{round}</div>
                    </div>
                    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-center" data-testid="rps-score-p2">
                        <div className="text-xs text-slate-400">{p2Name}</div>
                        <div className="text-3xl font-bold">{scores.p2}</div>
                    </div>
                </div>

                {/* Turn state */}
                {turn === 'p1' || turn === 'p2' ? (
                    <div className="text-center">
                        <div className="text-lg mb-1">
                            {turn === 'p1' ? p1Name : p2Name}'s pick — <span className="text-slate-400 text-sm">(other player, look away 🙈)</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mt-6">
                            {CHOICES.map((c) => (
                                <button
                                    key={c.key}
                                    onClick={() => pickChoice(c.key)}
                                    data-testid={`rps-choice-${c.key}`}
                                    className="rounded-2xl bg-white/5 hover:bg-white/15 border border-white/10 p-6 transition-all hover:scale-105"
                                >
                                    <div className="text-5xl mb-2">{c.emoji}</div>
                                    <div className="text-sm">{c.label}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : turn === 'reveal' ? (
                    <div className="text-center">
                        <div className="text-lg mb-4">Reveal!</div>
                        <div className="grid grid-cols-2 gap-6 mb-6">
                            <div className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid="rps-reveal-p1">
                                <div className="text-xs text-slate-400">{p1Name}</div>
                                <div className="text-6xl my-3">{CHOICES.find(c => c.key === p1Choice)?.emoji}</div>
                                <div className="text-sm">{CHOICES.find(c => c.key === p1Choice)?.label}</div>
                            </div>
                            <div className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid="rps-reveal-p2">
                                <div className="text-xs text-slate-400">{p2Name}</div>
                                <div className="text-6xl my-3">{CHOICES.find(c => c.key === p2Choice)?.emoji}</div>
                                <div className="text-sm">{CHOICES.find(c => c.key === p2Choice)?.label}</div>
                            </div>
                        </div>
                        <Button onClick={revealAndScore} className="w-full max-w-sm bg-gradient-to-r from-indigo-500 to-cyan-500" data-testid="rps-score-btn">
                            Score round
                        </Button>
                    </div>
                ) : turn === 'done' ? (
                    <div className="rounded-3xl bg-emerald-500/20 border border-emerald-400/40 p-6 text-center" data-testid="rps-winner-banner">
                        <div className="text-5xl mb-2">🏆</div>
                        <div className="text-2xl font-bold">
                            {winner === 'p1' ? p1Name : p2Name} wins the match!
                        </div>
                        <div className="text-sm text-emerald-200 mt-1">Win counted toward your rewards.</div>
                        <Button onClick={reset} className="mt-4" data-testid="rps-play-again">
                            <RotateCw className="w-4 h-4 mr-2" /> Play again
                        </Button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
